import {
  FORUM_THREAD_SURFACE_CLUB,
  FORUM_THREAD_SURFACE_COMMUNITY,
} from "./useForumThreadSurface";
import { THREAD_NAV_ORIGIN_COMMUNITY } from "./canonicalThreadNavigation";

function isActiveMember(membership) {
  if (!membership) return false;
  return String(membership.status || "").toLowerCase() === "active";
}

function isModRole(role) {
  const r = String(role || "").toLowerCase();
  return ["owner", "moderator", "mod", "admin"].includes(r);
}

function canUserModerateClubComments({ viewerId, membership, club }) {
  if (viewerId == null) return false;
  const ownerId = club?.owner_user_id ?? club?.ownerUserId;
  if (ownerId != null && Number(ownerId) === Number(viewerId)) return true;
  return isModRole(membership?.role);
}

function postHasClubFromPost(post) {
  if (!post) return false;
  const cid = Number(post.club_id);
  return Number.isFinite(cid) && cid > 0;
}

/**
 * Normalized forum thread surface: chrome, paths, and presentation strings.
 * Pure: keep call sites passing the same inputs previously used in `ClubPostThreadPage`.
 *
 * @param {object} input
 * @param {string} input.surface — from `useForumThreadSurface()`
 * @param {string|undefined} input.locationThreadOrigin — `location.state?.threadOrigin`
 * @param {object|null|undefined} input.post
 * @param {object|null|undefined} input.club
 * @param {object|null|undefined} input.membership
 * @param {string|undefined} input.slug — route param
 * @param {string} input.effectiveSlug — `String(slug || post?.club_slug || "").trim()`
 * @param {number|null} input.viewerId — from `pickViewerId(user)`
 */
export function buildForumThreadSurfaceContract({
  surface,
  locationThreadOrigin,
  post,
  club,
  membership,
  slug,
  effectiveSlug,
  viewerId,
}) {
  const postHasClub = postHasClubFromPost(post);

  const showClubChrome =
    surface === FORUM_THREAD_SURFACE_CLUB ? true : postHasClub;

  const showCommunityChrome = (() => {
    if (showClubChrome) return false;
    if (!post) return true;
    return !postHasClub;
  })();

  const openedFromCommunity =
    locationThreadOrigin === THREAD_NAV_ORIGIN_COMMUNITY ||
    surface === FORUM_THREAD_SURFACE_COMMUNITY;

  const accent = (() => {
    if (showCommunityChrome) return "emerald";
    const s = club?.settings && typeof club.settings === "object" ? club.settings : {};
    return s.accent === "sky" ? "sky" : "emerald";
  })();

  const canParticipate = showClubChrome
    ? isActiveMember(membership)
    : viewerId != null && Number.isFinite(Number(viewerId));

  const canModerateComments = showCommunityChrome
    ? false
    : canUserModerateClubComments({ viewerId, membership, club });

  const clubHomePath = (() => {
    if (showCommunityChrome) return "/community";
    if (!effectiveSlug) return "/community";
    return `/clubs/${encodeURIComponent(effectiveSlug)}?tab=feed`;
  })();

  const clubPublicPath = (() => {
    if (showCommunityChrome) return "/community";
    if (!effectiveSlug) return "/community";
    return `/clubs/${encodeURIComponent(effectiveSlug)}`;
  })();

  const threadBackTargetPath = openedFromCommunity ? "/community" : clubHomePath;

  const breadcrumbContextLabel = showCommunityChrome
    ? "Community"
    : club?.name || effectiveSlug || slug;

  const metaRowContextLabel = showCommunityChrome
    ? "Community"
    : club?.name || effectiveSlug || slug || "Club";

  const shareNativeTitle = showCommunityChrome
    ? "Community — thread"
    : club?.name
      ? `${club.name} — replies`
      : "Replies";

  const likeTitleWhenBlocked = showCommunityChrome ? "Log in to like" : "Join this club to like";

  const emptyCommentsSecondaryHint = showCommunityChrome
    ? "Log in to reply."
    : "Join the club to reply.";

  return {
    postHasClub,
    showCommunityChrome,
    accent,
    canParticipate,
    canModerateComments,
    clubHomePath,
    clubPublicPath,
    threadBackTargetPath,
    backNavWideLabel: openedFromCommunity ? "Back to Community" : "Back to club",
    breadcrumbContextLabel,
    metaRowContextLabel,
    shareNativeTitle,
    likeTitleWhenBlocked,
    emptyCommentsSecondaryHint,
  };
}
